import type { AriaAttributes, Ref } from 'react';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import type { ClassNameProp } from '../common-types';
import { Direction } from '../common-types';
import type { CommonInputProps } from './CommonInputProps';
import type { ValidIconName } from '../basic';
import { Icon } from '../basic';
import { noPropagationClickHandler } from '../../lib';
import { ValidationErrorMessage } from './internal';

/**
 * Props for the {@link Toggle} component.
 *
 * @author Bennett Piater
 */
export interface CheckboxProps
  extends ClassNameProp,
    CommonInputProps<boolean>,
    Pick<AriaAttributes, 'aria-label' | 'aria-describedby'> {
  /**
   * An optional custom color to use when the input is active.
   *
   * This is not supported by IE - if you need it outside the patient portal, use the classes as documented in {@link Toggle} and {@link Checkbox}.
   */
  color?: string;

  /**
   * An optional label component that may render more complex sub-components (like hyperlinks inside the label).
   *
   * This has to be seen as possible extension for the regular label (which is always used as aria-label text).
   */
  labelComponent?: JSX.Element;
  /**
   * Custom icon for checked value.
   */
  checkedIcon?: ValidIconName;
  /**
   * Custom icon for unchecked value.
   */
  uncheckedIcon?: ValidIconName;
  /**
   * Placement of the checkbox label.
   */
  labelPlacement?: Direction;
  /**
   * Whether checkbox should be in indeterminate state
   */
  indeterminate?: boolean;
  /**
   * Defines whether the checkbox should become active when clicked on intermediate state
   */
  setCheckedOnIndeterminateClick?: boolean;
}

/**
 * A Material Design Toggle component.
 *
 * @author agd
 * @author Bennett Piater
 */
const BooleanInput = forwardRef(function BooleanInput(
  {
    debounceWait = 1000,
    indeterminate = false,
    setCheckedOnIndeterminateClick = true,
    ...props
  }: CheckboxProps & {
    mode: 'toggle' | 'checkbox';
    indeterminate?: boolean;
  },
  ref: Ref<HTMLInputElement>
): JSX.Element {
  const controlled = props.value !== undefined;
  const [state, setState] = useState(props.value);
  useEffect(() => void setState(props.value), [props.value]);

  // decorate change callback event with debounce if necessary
  let onChange = props.onChange;
  if (!controlled && props.onChange && debounceWait > 0) {
    onChange = _.debounce(props.onChange, debounceWait);
  }

  const changeHandler = () => {
    if (props.disabled || props.readOnly) {
      return;
    }
    const newState =
      !indeterminate || (indeterminate && !setCheckedOnIndeterminateClick)
        ? !state
        : !!state;
    setState(newState);
    onChange?.(newState);
  };

  let wrapperClassName = `ehs-${props.mode}-wrapper`;
  if (props.className) wrapperClassName += ` ${props.className}`;

  const label = useMemo(
    () => (props.required ? `${props.label} *` : props.label),
    [props.label, props.required]
  );
  const labelPlacement = useMemo(
    () => props.labelPlacement || Direction.RIGHT,
    [props.labelPlacement]
  );

  let className = `ehs-${props.mode} label-${labelPlacement}`;
  if (props.error) className += ' invalid';
  else className += ' valid';
  if (props.disabled) className += ' disabled';

  // support custom colors
  const boxStyle = state && props.color ? { color: props.color } : undefined;
  const handleStyle =
    state && props.color ? { backgroundColor: props.color } : undefined;
  const trackStyle =
    state && props.color ? { ...handleStyle, opacity: 0.3 } : undefined;

  const icon = useMemo(() => {
    const uncheckedIcon =
      props.uncheckedIcon || 'materialicon-check_box_outline_blank';
    const checkedIcon = props.checkedIcon || 'materialicon-check_box';
    const indeterminateIcon = 'materialicon-indeterminate_check_box';

    if (!state) {
      return uncheckedIcon;
    } else if (indeterminate) {
      return indeterminateIcon;
    } else {
      return checkedIcon;
    }
  }, [indeterminate, props.checkedIcon, props.uncheckedIcon, state]);

  return (
    <div className={wrapperClassName}>
      <div
        className={className}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
        onClick={noPropagationClickHandler(changeHandler)}
      >
        <span>
          <input
            ref={ref}
            type="checkbox"
            disabled={props.disabled}
            readOnly={props.readOnly}
            defaultChecked={props.defaultValue}
            checked={
              props.value === undefined || indeterminate ? undefined : state
            }
            aria-required={props.required}
            aria-readonly={props.readOnly}
            aria-invalid={!!props.error}
            aria-label={props['aria-label'] ?? label}
            aria-checked={state && indeterminate ? 'mixed' : state}
            aria-describedby={props['aria-describedby']}
          />

          {props.mode === 'toggle' && (
            <>
              <span
                style={trackStyle}
                className={`track ${state ? 'active' : ''}`}
              />
              <span
                style={handleStyle}
                className={`handle ${state ? 'active' : ''}`}
              />
            </>
          )}

          {props.mode === 'checkbox' && (
            <Icon
              style={boxStyle}
              aria-label={undefined}
              className={`box ${state ? 'active' : ''}`.trim()}
              iconName={icon}
            />
          )}
        </span>

        <label aria-hidden={true}>{props.labelComponent ?? label}</label>
      </div>
      {props.error && <ValidationErrorMessage error={props.error} />}
    </div>
  );
});

/**
 * A Material Design Toggle component.
 *
 * If you want to set the color using a css class like in the health record, override the following:
 *
 * ```
 * .toggle .handle.active { background-color: ... }
 * .toggle .track.active { opacity: 0.3; background-color: ...}
 * ```
 *
 * @author agd
 * @author Bennett Piater
 */
export const Toggle = forwardRef(function Toggle(
  props: Omit<
    CheckboxProps,
    | 'checkedIcon'
    | 'uncheckedIcon'
    | 'indeterminate'
    | 'setCheckedOnIndeterminateClick'
  >,
  ref: Ref<HTMLInputElement>
): JSX.Element {
  return <BooleanInput {...props} mode="toggle" ref={ref} />;
});

/**
 * A Material Design Checkbox component.
 *
 * If you want to set the color using a css class like in the health record, override the following:
 *
 * ```
 * .checkbox .box.active { color: ... }
 * ```
 *
 * @author agd
 * @author Bennett Piater
 */
export const Checkbox = forwardRef(function Checkbox(
  props: CheckboxProps,
  ref: Ref<HTMLInputElement>
): JSX.Element {
  return <BooleanInput {...props} mode="checkbox" ref={ref} />;
});
